import { useMemo, useState } from 'react';
import {
  format,
  subDays,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
  subWeeks,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DateRange } from 'react-day-picker';
import { Calendar as CalendarIcon, Filter } from 'lucide-react';

import { FinancePageLayout, MoneyDisplay } from '@/components/finance';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { parseDateSafe } from '@/lib/timezoneUtils';
import { cn } from '@/lib/utils';
import { useStripeMirror } from '@/hooks/finance/useStripeMirror';

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Hoje' },
  { value: 'yesterday', label: 'Ontem' },
  { value: 'this_week', label: 'Esta semana' },
  { value: 'last_week', label: 'Semana passada' },
  { value: 'this_month', label: 'Este mês' },
  { value: 'last_month', label: 'Mês passado' },
  { value: 'custom', label: 'Selecionar período' },
];

const STRIPE_STATUSES = [
  { value: 'all', label: 'Todos' },
  { value: 'succeeded', label: 'Aprovado' },
  { value: 'failed', label: 'Falhou' },
  { value: 'pending', label: 'Pendente' },
  { value: 'processing', label: 'Processando' },
  { value: 'requires_payment_method', label: 'Sem método válido' },
  { value: 'canceled', label: 'Cancelado' },
];

const PROCESSING_STATUSES = [
  { value: 'all', label: 'Todos' },
  { value: 'pending', label: 'Na fila' },
  { value: 'processed', label: 'Processado' },
  { value: 'failed', label: 'Falhou' },
  { value: 'ignored', label: 'Ignorado' },
];

const MATERIALIZATION_STATUSES = [
  { value: 'all', label: 'Todos' },
  { value: 'materialized', label: 'Materializadas' },
  { value: 'unmaterialized', label: 'Não materializadas' },
];

const LINK_STATUSES = [
  { value: 'all', label: 'Todos' },
  { value: 'linked', label: 'Vinculadas' },
  { value: 'unlinked', label: 'Sem vínculo' },
];

const getDateRange = (period: string, customRange?: DateRange) => {
  const today = new Date();
  switch (period) {
    case 'today':
      return { start: startOfDay(today), end: endOfDay(today) };
    case 'yesterday': {
      const yesterday = subDays(today, 1);
      return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
    }
    case 'this_week':
      return { start: startOfWeek(today, { weekStartsOn: 1 }), end: endOfDay(today) };
    case 'last_week': {
      const lastWeek = subWeeks(today, 1);
      return {
        start: startOfWeek(lastWeek, { weekStartsOn: 1 }),
        end: endOfWeek(lastWeek, { weekStartsOn: 1 }),
      };
    }
    case 'this_month':
      return { start: startOfMonth(today), end: endOfDay(today) };
    case 'last_month': {
      const lastMonth = subMonths(today, 1);
      return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
    }
    case 'custom':
      return {
        start: customRange?.from || subDays(today, 30),
        end: customRange?.to || today,
      };
    default:
      return { start: subDays(today, 30), end: today };
  }
};

function getStripeStatusBadge(status?: string | null) {
  switch (status) {
    case 'succeeded':
      return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Aprovado</Badge>;
    case 'failed':
      return <Badge className="bg-red-500/10 text-red-600 border-red-500/20">Falhou</Badge>;
    case 'pending':
      return <Badge className="bg-yellow-500/10 text-yellow-700 border-yellow-500/20">Pendente</Badge>;
    case 'processing':
      return <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">Processando</Badge>;
    case 'requires_payment_method':
      return <Badge className="bg-orange-500/10 text-orange-600 border-orange-500/20">Sem método</Badge>;
    case 'canceled':
      return <Badge className="bg-slate-500/10 text-slate-600 border-slate-500/20">Cancelado</Badge>;
    default:
      return <Badge variant="outline">{status || 'unknown'}</Badge>;
  }
}

function getProcessingBadge(status: string) {
  switch (status) {
    case 'processed':
      return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Processado</Badge>;
    case 'pending':
      return <Badge className="bg-yellow-500/10 text-yellow-700 border-yellow-500/20">Na fila</Badge>;
    case 'failed':
      return <Badge className="bg-red-500/10 text-red-600 border-red-500/20">Falhou</Badge>;
    case 'ignored':
      return <Badge className="bg-slate-500/10 text-slate-600 border-slate-500/20">Ignorado</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function getMaterializationBadge(materialized: boolean) {
  if (materialized) {
    return <Badge className="bg-emerald-500/10 text-emerald-700 border-emerald-500/20">Materializada</Badge>;
  }
  return <Badge className="bg-amber-500/10 text-amber-700 border-amber-500/20">Não materializada</Badge>;
}

function getLinkBadge(linked: boolean) {
  if (linked) {
    return <Badge className="bg-slate-500/10 text-slate-700 border-slate-400/20">Vinculada</Badge>;
  }
  return <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">Sem vínculo</Badge>;
}

export default function StripeMirror() {
  const [periodFilter, setPeriodFilter] = useState('this_month');
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>();
  const [stripeStatus, setStripeStatus] = useState('all');
  const [processingStatus, setProcessingStatus] = useState('all');
  const [materializationStatus, setMaterializationStatus] = useState('all');
  const [linkStatus, setLinkStatus] = useState('all');
  const [search, setSearch] = useState('');

  const dateRange = useMemo(() => getDateRange(periodFilter, customDateRange), [periodFilter, customDateRange]);
  const { data: records, isLoading } = useStripeMirror({
    startDate: format(dateRange.start, 'yyyy-MM-dd'),
    endDate: format(dateRange.end, 'yyyy-MM-dd'),
    stripeStatus,
    processingStatus,
    materializationStatus,
    linkStatus,
    search,
  });

  const summary = useMemo(() => {
    if (!records) {
      return { total: 0, approved: 0, failed: 0, materialized: 0 };
    }

    return {
      total: records.length,
      approved: records.filter((record) => record.stripe_status === 'succeeded').length,
      failed: records.filter((record) => record.stripe_status === 'failed').length,
      materialized: records.filter((record) => record.has_materialized_ledger).length,
    };
  }, [records]);

  return (
    <FinancePageLayout>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Stripe</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Espelho completo do provedor, com aprovadas, rejeitadas, pendentes e materializadas.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <CalendarIcon className="h-4 w-4" />
                Período
              </Label>
              <Select value={periodFilter} onValueChange={setPeriodFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map((period) => (
                    <SelectItem key={period.value} value={period.value}>
                      {period.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {periodFilter === 'custom' ? (
              <div className="space-y-2">
                <Label>Intervalo</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !customDateRange?.from && 'text-muted-foreground',
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {customDateRange?.from ? (
                        customDateRange.to ? (
                          `${format(customDateRange.from, 'dd/MM/yyyy')} - ${format(customDateRange.to, 'dd/MM/yyyy')}`
                        ) : format(customDateRange.from, 'dd/MM/yyyy')
                      ) : (
                        'Selecione as datas'
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      selected={customDateRange}
                      onSelect={setCustomDateRange}
                      numberOfMonths={2}
                      locale={ptBR}
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            ) : null}

            <div className="space-y-2">
              <Label>Status Stripe</Label>
              <Select value={stripeStatus} onValueChange={setStripeStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STRIPE_STATUSES.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Processamento</Label>
              <Select value={processingStatus} onValueChange={setProcessingStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROCESSING_STATUSES.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Materialização</Label>
              <Select value={materializationStatus} onValueChange={setMaterializationStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MATERIALIZATION_STATUSES.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Vínculo</Label>
              <Select value={linkStatus} onValueChange={setLinkStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LINK_STATUSES.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-4">
            <Label>Buscar por cliente, deal, customer, payment intent, charge ou referência</Label>
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Ex.: Lucas, cus_..., pi_..., ch_..."
              className="mt-2"
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total de Eventos</CardDescription>
            <CardTitle className="text-2xl">
              {isLoading ? <Skeleton className="h-8 w-24" /> : summary.total}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Aprovadas</CardDescription>
            <CardTitle className="text-2xl text-green-600">
              {isLoading ? <Skeleton className="h-8 w-24" /> : summary.approved}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Falhas</CardDescription>
            <CardTitle className="text-2xl text-red-600">
              {isLoading ? <Skeleton className="h-8 w-24" /> : summary.failed}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Materializadas no Financeiro</CardDescription>
            <CardTitle className="text-2xl text-emerald-700">
              {isLoading ? <Skeleton className="h-8 w-24" /> : summary.materialized}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Deal</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Status Stripe</TableHead>
                  <TableHead>Processamento</TableHead>
                  <TableHead>Materialização</TableHead>
                  <TableHead>Vínculo</TableHead>
                  <TableHead>Referência</TableHead>
                  <TableHead>Stripe ID</TableHead>
                  <TableHead>Customer</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      {Array.from({ length: 11 }).map((__, cellIndex) => (
                        <TableCell key={cellIndex}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : records?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                      Nenhum evento Stripe encontrado para os filtros selecionados
                    </TableCell>
                  </TableRow>
                ) : (
                  records?.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>
                        {(() => {
                          const txDate = parseDateSafe(record.transaction_date);
                          return txDate ? format(txDate, 'dd/MM/yyyy', { locale: ptBR }) : '-';
                        })()}
                      </TableCell>
                      <TableCell className="font-medium">{record.contact_name}</TableCell>
                      <TableCell>{record.deal_name}</TableCell>
                      <TableCell className="text-right font-mono">
                        <MoneyDisplay value={record.amount} currency={record.currency === 'BRL' ? 'BRL' : 'USD'} />
                      </TableCell>
                      <TableCell>{getStripeStatusBadge(record.stripe_status)}</TableCell>
                      <TableCell>{getProcessingBadge(record.processing_status)}</TableCell>
                      <TableCell>{getMaterializationBadge(record.has_materialized_ledger)}</TableCell>
                      <TableCell>{getLinkBadge(Boolean(record.linked_payment_transaction_id || record.linked_payment_id))}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {record.reference_number || record.charge_id || '-'}
                        {record.review_reason ? (
                          <div className="mt-1 text-xs text-muted-foreground">{record.review_reason}</div>
                        ) : null}
                      </TableCell>
                      <TableCell className="max-w-[220px] truncate font-mono text-xs text-muted-foreground">
                        {record.payment_intent_id || record.charge_id || '-'}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {record.customer_id || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </FinancePageLayout>
  );
}
